"use client";

import type {
  AliyunCaptchaInitOptions,
  AliyunCaptchaInstance,
  CaptchaVerifyResult,
} from "@/common/aliyun-captcha";
import { CAPTCHA_CONFIG } from "@/common/aliyun-captcha";
import { useCallback, useEffect, useMemo, useRef } from "react";

interface UseAliyunCaptchaOptions {
  /** 验证码验证回调 */
  onVerify: (captchaVerifyParam: string) => Promise<CaptchaVerifyResult>;
  /** 验证码元素ID */
}

export function useAliyunCaptcha({ onVerify }: UseAliyunCaptchaOptions) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<AliyunCaptchaInstance>(null);

  const [elementId, buttonId] = useMemo(() => {
    const elementId = `captcha-element-${Date.now()}`;
    const buttonId = `captcha-button-id-${Date.now()}`;
    return [elementId, buttonId];
  }, []);

  // Create DOM elements on client side only
  useEffect(() => {
    if (typeof window === "undefined") return;

    elementRef.current = window.document.createElement("div");
    elementRef.current.id = elementId;
    window.document.body.appendChild(elementRef.current);

    buttonRef.current = window.document.createElement("button");
    buttonRef.current.id = buttonId;
    window.document.body.appendChild(buttonRef.current);

    return () => {
      // Cleanup on unmount
      elementRef.current?.remove();
      buttonRef.current?.remove();
    };
  }, [elementId, buttonId]);

  // 初始化验证码
  const initCaptcha = useCallback(() => {
    if (typeof window === "undefined") return;

    if (!window.initAliyunCaptcha) {
      console.error("Aliyun Captcha SDK not loaded");
      return;
    }

    if (!CAPTCHA_CONFIG.sceneId) {
      console.error("Captcha SceneId not configured");
      return;
    }

    const options: AliyunCaptchaInitOptions = {
      SceneId: CAPTCHA_CONFIG.sceneId,
      mode: "popup",
      element: `#${elementId}`,
      button: `#${buttonId}`,
      slideStyle: {
        width: 360,
        height: 40,
      },
      captchaVerifyCallback: async (captchaVerifyParam: string) => {
        const result = await onVerify(captchaVerifyParam);
        return result;
      },
      getInstance: (instance: AliyunCaptchaInstance) => {
        instanceRef.current = instance;
      },
      language: "en",
    };

    window.initAliyunCaptcha(options);
  }, [buttonId, elementId, onVerify]);

  useEffect(() => {
    initCaptcha();
  }, [initCaptcha]);

  const handleVerify = useCallback(async () => {
    if (!instanceRef.current) {
      initCaptcha();
    }
    buttonRef.current?.click();
  }, [initCaptcha]);

  return {
    elementId,
    buttonId,
    handleVerify,
  };
}
